import styled from "styled-components";
import { Check, X, Megaphone, AlertTriangle } from "lucide-react";

import { theme } from "./theme";
import { useT } from "./i18n";

// The last thing between a walk-in and a wrong date.
//
// A generic "create this booking?" confirm does not work here, because the date is exactly the field
// nobody re-reads — the system rolled the day at midnight and the FO clicked through. This dialog
// makes the details something you say OUT LOUD to the guest, who is standing right there and will
// contradict a wrong date. That is the only point in the flow where a second pair of eyes exists.
const Backdrop = styled.div`
  align-items: center;
  background: rgba(15, 23, 42, 0.6);
  display: flex;
  inset: 0;
  justify-content: center;
  padding: 1rem;
  position: fixed;
  z-index: 1600;
`;

const Card = styled.div`
  background: #ffffff;
  border-radius: 14px;
  border-top: 3px solid ${theme.gold};
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.35);
  display: flex;
  flex-direction: column;
  max-height: 92vh;
  max-width: 560px;
  width: 100%;
`;

const Head = styled.div`
  align-items: center;
  border-bottom: 1px solid ${theme.border};
  display: flex;
  gap: 0.45rem;
  padding: 0.85rem 1.1rem;
`;

const Title = styled.h3`
  align-items: center;
  color: ${theme.ink};
  display: flex;
  flex: 1 1 auto;
  font-size: 1.02rem;
  font-weight: 800;
  gap: 0.4rem;
  margin: 0;
`;

const Close = styled.button`
  background: transparent;
  border: 0;
  color: ${theme.ink700};
  cursor: pointer;
  padding: 0.1rem;
`;

const Body = styled.div`
  overflow-y: auto;
  padding: 0.9rem 1.1rem;
`;

// The script is set apart and larger than the rest: it is meant to be read, not skimmed.
const Script = styled.p`
  background: ${theme.goldSoft};
  border-radius: 10px;
  color: ${theme.ink};
  font-size: 1rem;
  font-weight: 700;
  line-height: 1.5;
  margin: 0 0 0.8rem;
  padding: 0.7rem 0.8rem;
`;

const Points = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
`;

const Point = styled.li`
  border-bottom: 1px solid #f1efe9;
  display: flex;
  gap: 0.8rem;
  justify-content: space-between;
  padding: 0.5rem 0;

  &:last-child {
    border-bottom: 0;
  }
`;

const PointLabel = styled.span`
  color: #4a4f63;
  font-size: 0.84rem;
  font-weight: 700;
  flex: 0 0 auto;
`;

const PointValue = styled.span`
  color: ${(props) => (props.$emphasis ? theme.ink : "#2f2f2f")};
  font-size: ${(props) => (props.$emphasis ? "1rem" : "0.9rem")};
  font-weight: ${(props) => (props.$emphasis ? 800 : 600)};
  text-align: right;
`;

const DateWarning = styled.div`
  align-items: flex-start;
  background: #fdf3e3;
  border-radius: 9px;
  color: #8a5a00;
  display: flex;
  font-size: 0.84rem;
  font-weight: 700;
  gap: 0.4rem;
  line-height: 1.45;
  margin-top: 0.8rem;
  padding: 0.6rem 0.7rem;
`;

const Foot = styled.div`
  border-top: 1px solid ${theme.border};
  display: flex;
  gap: 0.5rem;
  justify-content: flex-end;
  padding: 0.75rem 1.1rem;
`;

const Btn = styled.button`
  align-items: center;
  border-radius: 8px;
  cursor: pointer;
  display: inline-flex;
  font-size: 0.88rem;
  font-weight: 800;
  gap: 0.35rem;
  padding: 0.55rem 1rem;
`;

const Ghost = styled(Btn)`
  background: #ffffff;
  border: 1px solid ${theme.border};
  color: ${theme.ink700};
`;

const Ok = styled(Btn)`
  background: ${theme.gold};
  border: 0;
  color: #ffffff;

  &:hover {
    background: ${theme.goldDark};
  }
`;

// props: { script, points: [{label, value, emphasis}], warnings: [], onConfirm, onCancel }
const ReadAloudDialog = (props) => {
  const { t } = useT();

  return (
    <Backdrop onClick={props.onCancel}>
      <Card onClick={(event) => event.stopPropagation()}>
        <Head>
          <Title>
            <Megaphone size={17} />
            {t("read_aloud_title")}
          </Title>
          <Close type="button" aria-label={t("close")} onClick={props.onCancel}>
            <X size={18} />
          </Close>
        </Head>

        <Body>
          <Script>{props.script}</Script>
          <Points>
            {(props.points || []).map((point) => (
              <Point key={point.label}>
                <PointLabel>{point.label}</PointLabel>
                <PointValue $emphasis={point.emphasis}>
                  {point.value}
                </PointValue>
              </Point>
            ))}
          </Points>
          {/* A LIST, not one string: a late-night walk-in that also picks a past transit window needs
              to hear both, and an exclusive ternary would silently drop the second. */}
          {(props.warnings || []).filter(Boolean).map((warning) => (
            <DateWarning key={warning}>
              <AlertTriangle size={16} style={{ flex: "0 0 auto" }} />
              <span>{warning}</span>
            </DateWarning>
          ))}
        </Body>

        <Foot>
          <Ghost type="button" onClick={props.onCancel}>
            <X size={15} /> {t("read_aloud_back")}
          </Ghost>
          <Ok type="button" onClick={props.onConfirm}>
            <Check size={15} /> {t("read_aloud_ok")}
          </Ok>
        </Foot>
      </Card>
    </Backdrop>
  );
};

export default ReadAloudDialog;
